import camelcaseKeys from "camelcase-keys";
import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import Vue from "vue";
import SubscriptionPlan, {
  CommisionSubscriptionPlan,
  mapSubscriptionPlan,
  SubscriptionBillingType,
} from "@/models/subscription/SubscriptionPlan";

/**
 * 🧩 Module which is responsible for handling the subscription and subscription limits
 */
@Module
export default class SubscriptionModule extends VuexModule {
  /**
   * 📦 The current subscription plan of the user
   */
  public currentSubscriptionPlan: SubscriptionPlan | null = null;

  /**
   * 📦 The available subscriptions for the platform
   */
  public availableSubscriptionPlans: SubscriptionPlan[] = [];

  /**
   * 🎁 Wrapper object that aims to map the current subscription plan with the available ones that have priority
   */
  public get currentSubscriptionPlanWithPriority():
    | SubscriptionPlan
    | undefined {
    return this.availableSubscriptionPlans.find(
      (plan: SubscriptionPlan) =>
        plan.type == this.currentSubscriptionPlan?.type
    );
  }

  /**
   * 🔨 Getter function that allows a plan to be searched by price Id
   */
  public get getSubscriptionPlanBasedOnPriceId(): (
    priceId: string
  ) => SubscriptionPlan | undefined {
    return (priceId: string) => {
      return this.availableSubscriptionPlans.find((plan: SubscriptionPlan) => {
        return plan.priceIds.includes(priceId);
      });
    };
  }

  /**
   * 📦 The commission percentage that the platform takes from the profits
   */
  public get platformCommissionPercentage(): number | null {
    if (this.currentSubscriptionPlan instanceof CommisionSubscriptionPlan) {
      return this.currentSubscriptionPlan.commissionPercentage * 100;
    }
    return null;
  }

  /**
   * 🚩 Flag for whether the subscription is "Paid"
   */
  public get isSubscriptionPaid(): boolean {
    return (
      this.currentSubscriptionPlan?.billingType !==
        SubscriptionBillingType.Free || this.currentSubscriptionPlan?.freePro
    );
  }

  /**
   * 🚩 Flag for whether the subscription is free or a free pro
   */
  public get isFreeOrFreeProSubscription(): boolean {
    return (
      this.currentSubscriptionPlan != undefined &&
      this.currentSubscriptionPlan != null &&
      (this.currentSubscriptionPlan.billingType ===
        SubscriptionBillingType.Free ||
        this.currentSubscriptionPlan.freePro)
    );
  }

  /**
   * 👾 Mutation which sets the current subscription plan
   * @param subscriptionPlan
   */
  @Mutation
  private setCurrentSubscriptionPlan(subscriptionPlan: SubscriptionPlan) {
    this.currentSubscriptionPlan = subscriptionPlan;
  }

  /**
   * 👾 Mutation which sets the available subscriptions
   * @param plans
   */
  @Mutation
  private setAvailableSubscriptionPlans(plans: SubscriptionPlan[]) {
    this.availableSubscriptionPlans = plans;
  }

  /**
   * 🚀 Fetches the current subscription's limits from the API
   */
  @Action
  public async fetchCurrentSubscriptionPlan() {
    const response = await Vue.prototype.$api.get("/api/v1/subscription");
    this.context.commit(
      "setCurrentSubscriptionPlan",
      mapSubscriptionPlan(camelcaseKeys(response.data.current) as any)
    );
  }

  /**
   * 🚀 Fetches the available subscription plans
   */
  @Action
  public async fetchAvailableSubscriptionPlans() {
    const response = await Vue.prototype.$api.get(
      "/api/v1/availablesubscriptions"
    );

    let availablePlans = (camelcaseKeys(response.data) as any[]).map(
      (rawSubscriptionPlan: any, index: number) => {
        return mapSubscriptionPlan(rawSubscriptionPlan, index);
      }
    );

    availablePlans = availablePlans.sort(
      (planA: SubscriptionPlan, planB: SubscriptionPlan) =>
        planA.priority - planB.priority
    );

    this.context.commit("setAvailableSubscriptionPlans", availablePlans);
  }
}
